import _ from 'lodash';
import ApolloClientUtils from 'shared/utils/apollo-client-utils';
import { UIState } from 'src/state/ui';
import { FeatureFlagsState } from 'src/state/feature-flags';
import { UserState } from 'src/state/user';
import { PusherState } from 'src/state/pusher';
import { StreamState } from 'src/state/stream';
import AdminPublicEnv, { getBackofficeHostedRequestHeaders } from 'src/utils/admin-public-env';
import { redirectToBackofficeLogin } from 'src/utils/backoffice-auth';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

// rationale: legacy support

function onSessionExpired(): void {
  window.localStorage.removeItem('access-token');

  if (AdminPublicEnv.isBackofficeHosted) {
    redirectToBackofficeLogin();
    return;
  }

  window.location.href = '/login';
}

type RootStateStores = {
  apolloClient: ApolloClient<NormalizedCacheObject>;
  FeatureFlags: FeatureFlagsState;
  Pusher: PusherState;
  SpecialsState?: Record<any, any>;
  Stream: StreamState;
  UI: UIState;
  User: UserState;
};

export class RootState {
  readonly FeatureFlags = new FeatureFlagsState(this);
  readonly UI = new UIState(this);
  readonly apolloClient = ApolloClientUtils.setupMainApolloClient('admin', {
    // @ts-expect-error onAuthError not in type definition but needed at runtime
    onAuthError: _.noop, // eslint-disable-line @typescript-eslint/unbound-method
    onSessionExpired,
    uri: AdminPublicEnv.graphqlUri,
    useGETForHashedQueries: AdminPublicEnv.useGetForHashedQueries,
    getRequestHeaders: getBackofficeHostedRequestHeaders,
    onUpdateVersion: (latestVersion: string) => {
      this.UI.updateLatestVersion(latestVersion);
    },
  });
  readonly Pusher = new PusherState(this, AdminPublicEnv.pusherKey, RootState.getToken());
  readonly Stream = new StreamState(this);
  readonly User = new UserState(this);

  private static getToken(): string | undefined {
    return window.localStorage.getItem('access-token') ?? undefined;
  }

  stores(): RootStateStores {
    return {
      apolloClient: this.apolloClient,
      FeatureFlags: this.FeatureFlags,
      Pusher: this.Pusher,
      Stream: this.Stream,
      UI: this.UI,
      User: this.User,
    };
  }
}

// Root must evaluate the hosted LaunchDarkly gate before this state exists:
// RootState construction initializes Apollo and starts UserState session bootstrap.
// Keep imports lazy so flag-off mode creates neither transport nor session work.
let appStateInstance: RootState | undefined;

export function getAppState(): RootState {
  if (!appStateInstance) {
    appStateInstance = new RootState();
    window._____APP_STATE_____ = appStateInstance;
  }

  return appStateInstance;
}

export const appState = new Proxy({} as RootState, {
  get: (_target, property) => {
    const state = getAppState();
    const value = Reflect.get(state, property, state);
    return typeof value === 'function' ? value.bind(state) : value;
  },
  set: (_target, property, value) => Reflect.set(getAppState(), property, value),
});

export type RootStore = ReturnType<RootState['stores']>;
